import { useQuery } from "@tanstack/react-query";
import type { ProjectDetailDTO, ProjectMemberRole } from "@app-factory/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/auth";

/**
 * Query hooky sekcí projektu. Klíč ["section", projectId, sekce] odpovídá
 * realtime eventu project.section.changed — invalidace je cílená.
 */
export function useApplications(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "applications"],
    queryFn: () => api.applications(projectId),
  });
}

export function useUploads(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "uploads"],
    queryFn: () => api.uploads(projectId),
  });
}

export function useKnowledgeTree(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "knowledge"],
    queryFn: () => api.knowledgeTree(projectId),
  });
}

export function useConflicts(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "conflicts"],
    queryFn: () => api.conflicts(projectId),
  });
}

export function useCodebases(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "codebases"],
    queryFn: () => api.codebases(projectId),
  });
}

export function useEnvironments(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "environments"],
    queryFn: () => api.environments(projectId),
  });
}

export function usePolicies(projectId: string) {
  return useQuery({
    queryKey: ["section", projectId, "policies"],
    queryFn: () => api.policies(projectId),
  });
}

export type EffectiveRole = ProjectMemberRole | "OWNER" | null;

/** Efektivní role aktuálního uživatele v projektu (zrcadlí serverovou matici). */
export function useProjectRole(project: ProjectDetailDTO): EffectiveRole {
  const { session } = useAuth();
  if (!session) return null;
  if (session.user.role === "OWNER") return "OWNER";
  return project.members.find((m) => m.userId === session.user.id)?.role ?? null;
}

export function canContribute(role: EffectiveRole): boolean {
  return role === "OWNER" || role === "MANAGER" || role === "COLLABORATOR";
}

export function canManage(role: EffectiveRole): boolean {
  return role === "OWNER" || role === "MANAGER";
}
